import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';

/**
 * SF-122 Component 3.5: one warning line, used in the Alerts card — the `AlertBanners.tsx`
 * replacement. Colors and role mapping preserved exactly from the deleted component. The
 * critical branch's colors exactly match existing named tokens (COLORS.errorBg/errorBorder/
 * negative), so it references them structurally (SF-125 convention) rather than re-typing the
 * literal; the warning branch's amber has no matching token in theme.ts and stays literal.
 *
 * `actionRoute`/`actionLabel` (optional): a deep link to the page where the player can act on
 * this alert (e.g. barren acres → Buildings). Omitted when an alert has no distinct action page
 * (e.g. "Army is away" is already visible via the Armies Away card on the same screen).
 *
 * SF-153: widened with a third, additive `'info'` severity for a neutral "nothing is happening"
 * empty state (e.g. Dragons' "no active project" message) — `'warning'` was the wrong tone since
 * that state isn't a problem for most kingdom members. Backward-compatible: every existing
 * `'critical'`/`'warning'` consumer is unaffected.
 */
interface AlertRowProps {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  actionRoute?: string;
  actionLabel?: string;
}

export function AlertRow({ severity, message, actionRoute, actionLabel }: AlertRowProps) {
  const isCritical = severity === 'critical';
  const isInfo = severity === 'info';
  const color = isCritical ? COLORS.negative : isInfo ? COLORS.text.tertiary : '#ffbb44';
  const background = isCritical ? COLORS.errorBg : isInfo ? COLORS.card : '#2a2414';
  const border = isCritical ? COLORS.errorBorder : isInfo ? COLORS.border : '#4a3a20';

  return (
    <div
      className="px-3 py-2 rounded-md text-[13px] flex items-center justify-between gap-3"
      style={{
        background,
        border: `1px solid ${border}`,
        color,
      }}
      role={isCritical ? 'alert' : 'status'}
    >
      <span>{message}</span>
      {actionRoute && (
        <Link
          to={actionRoute}
          className="flex-shrink-0 font-semibold whitespace-nowrap underline"
          style={{ color }}
        >
          {actionLabel ?? 'Fix'} →
        </Link>
      )}
    </div>
  );
}
